// Inline auto-format-as-you-type. Mirrors Slack's composer
// behavior: when the user finishes a delimited token, the
// delimiters are consumed and the inner text becomes the
// corresponding semantic element. Patterns:
//
//   *foo*           → <strong>foo</strong>
//   _foo_           → <em>foo</em>
//   ~foo~           → <s>foo</s>
//   `foo`           → <code>foo</code>
//   [label](url)    → <a href="url">label</a>
//   https://x.com   → <a href="https://x.com">https://x.com</a>  (on space)
//
// Each handler runs on the text node holding the caret, after a
// terminating keystroke. We never touch text the user typed
// earlier — only the most recently-completed token. Operations
// preserve the caret by re-creating a Range that lands just AFTER
// the inserted element.
//
// Skipping rules:
//   - inside <code> or <pre> the editor is verbatim — no auto-format
//   - inside an existing <a> we don't re-detect URLs
//   - empty inner content (`**`, `__`, `~~`, ` `` `, `[](url)`) skips
//   - a space immediately after the opener (`* foo*`) skips
//   - a space immediately before the closer (`*foo *`) skips
//
// These match Slack's published rules and feel "live" without
// false positives on partial / aborted formatting attempts.

const MARK_TAG: Readonly<Record<string, string>> = {
  '*': 'strong',
  _: 'em',
  '~': 's',
  '`': 'code',
};

// URL detector reused by the on-space autolink path. Same pattern
// as the paste autolinker, kept inline so this module has no
// import cycle with `./paste`.
const URL_RE =
  /(?:\b(?:https?:\/\/|mailto:)[^\s<>"']+|\bwww\.[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:\/[^\s<>"']*)?)/i;

// Bare-domain autolink (no scheme, no `www.`) — e.g. `test.com`,
// `example.co.uk/path`. To avoid false positives on `Node.js`,
// `index.html`, `file.txt`, `3.14`, the final label (TLD) must be one of
// a curated set of common gTLDs / ccTLDs. The user can always type a
// full `https://` URL for anything outside the set.
const BARE_DOMAIN_RE =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+([a-z]{2,24})(?:\/[^\s<>"']*)?$/i;
const COMMON_TLDS: ReadonlySet<string> = new Set([
  'com', 'org', 'net', 'io', 'co', 'dev', 'app', 'ai', 'xyz', 'me', 'gg',
  'tv', 'info', 'biz', 'online', 'site', 'tech', 'store', 'blog', 'news',
  'cloud', 'page', 'link', 'live', 'chat', 'edu', 'gov', 'mil', 'uk', 'us',
  'ca', 'de', 'fr', 'es', 'it', 'nl', 'ru', 'jp', 'cn', 'in', 'br', 'au',
  'se', 'no', 'fi', 'dk', 'ch', 'at', 'be', 'pl', 'pt', 'cz', 'gr', 'ie',
  'nz', 'za', 'mx', 'kr', 'sg', 'hk', 'tw', 'ae', 'sa', 'tr', 'ua', 'id',
  'ph', 'my', 'th', 'vn',
]);

/**
 * Inspect the caret position and rewrite the most recently-typed
 * delimited token. Returns `true` when the DOM was mutated so the
 * caller knows to re-emit `onChangeMarkdown`.
 *
 * `triggerChar` is the character the user just typed (the keypress
 * event's `key` or the trailing character of the just-inserted
 * text). Different triggers handle different patterns:
 *
 *   `*` `_` `~` `` ` ``  → inline-mark closer
 *   `)`                  → link closer (looks for `[label](url)`)
 *   ` ` `\n` `\t`        → URL autolink (looks for a URL token
 *                          immediately before the whitespace)
 */
export function applyInlineAutoFormat(
  root: HTMLElement,
  selection: Selection | null,
  triggerChar: string,
): boolean {
  if (selection === null || selection.rangeCount === 0) return false;
  if (!selection.isCollapsed) return false;
  const range = selection.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return false;
  if (!root.contains(node)) return false;
  if (isInsideVerbatim(node, root)) return false;
  const caret = range.startOffset;

  if (triggerChar in MARK_TAG) {
    return tryMarkAutoFormat(
      node as Text,
      caret,
      triggerChar,
      MARK_TAG[triggerChar] ?? 'strong',
      selection,
    );
  }
  if (triggerChar === ')') {
    return tryLinkAutoFormat(node as Text, caret, selection);
  }
  if (triggerChar === ' ' || triggerChar === '\n' || triggerChar === '\t') {
    return tryUrlAutoLink(node as Text, caret, selection);
  }
  return false;
}

// Emoticon → emoji table, longest-first so `:-)` wins over `:)` and
// `:'(` wins over `:(`. Mirrors Slack's default-on "Convert my typed
// emoticons to emoji". The terminal character of each pattern is the
// keystroke that fires the conversion.
const EMOTICONS: ReadonlyArray<readonly [string, string]> = [
  [":'(", '😢'],
  [':-)', '🙂'],
  [':-(', '🙁'],
  [';-)', '😉'],
  [':-D', '😄'],
  [':-P', '😛'],
  [':-p', '😛'],
  [':-O', '😮'],
  [':-/', '😕'],
  [':)', '🙂'],
  [':(', '🙁'],
  [';)', '😉'],
  [':D', '😄'],
  [':P', '😛'],
  [':p', '😛'],
  [':O', '😮'],
  [':o', '😮'],
  [':/', '😕'],
  [':|', '😐'],
  ['<3', '❤️'],
];

/**
 * Convert a just-completed text emoticon at the caret into its emoji
 * glyph. `inserted` is the character the user just typed (the
 * emoticon's terminal char). Returns `true` when the DOM was mutated.
 *
 * Guards:
 *   - never inside inline code / code block / link (verbatim),
 *   - the emoticon must be preceded by start-of-node or whitespace, so
 *     it can't fire inside a word or a URL (`https://` → `:/` is safe).
 */
export function applyEmoticon(
  root: HTMLElement,
  selection: Selection | null,
  inserted: string,
): boolean {
  if (selection === null || selection.rangeCount === 0) return false;
  if (!selection.isCollapsed) return false;
  const range = selection.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return false;
  if (!root.contains(node)) return false;
  if (isInsideVerbatim(node, root)) return false;
  const caret = range.startOffset;
  const text = (node as Text).nodeValue ?? '';
  for (const [pattern, glyph] of EMOTICONS) {
    if (pattern[pattern.length - 1] !== inserted) continue;
    const len = pattern.length;
    if (caret < len) continue;
    if (text.slice(caret - len, caret) !== pattern) continue;
    const beforeIdx = caret - len - 1;
    const before = beforeIdx >= 0 ? text[beforeIdx] : undefined;
    if (before !== undefined && before !== ' ' && before !== '\t' && before !== '\n') {
      continue;
    }
    const t = node as Text;
    t.replaceData(caret - len, len, glyph);
    const doc = t.ownerDocument;
    if (doc === null) return true;
    const r = doc.createRange();
    r.setStart(t, caret - len + glyph.length);
    r.collapse(true);
    selection.removeAllRanges();
    selection.addRange(r);
    return true;
  }
  return false;
}

/** Walks ancestors looking for an element we don't auto-format inside. */
function isInsideVerbatim(node: Node, root: HTMLElement): boolean {
  let cur: Node | null = node;
  while (cur !== null && cur !== root) {
    if (cur.nodeType === Node.ELEMENT_NODE) {
      const tag = (cur as HTMLElement).tagName;
      if (tag === 'CODE' || tag === 'PRE' || tag === 'A') return true;
    }
    cur = cur.parentNode;
  }
  return false;
}

function tryMarkAutoFormat(
  node: Text,
  caret: number,
  marker: string,
  tagName: string,
  selection: Selection,
): boolean {
  const text = node.nodeValue ?? '';
  // The just-typed marker must sit at `caret - 1`.
  if (caret < 3) return false;
  if (text[caret - 1] !== marker) return false;
  // Skip "*foo *" — a space immediately before the closer means the
  // user backed off (Slack ignores it).
  if (text[caret - 2] === ' ' || text[caret - 2] === '\t') return false;

  // Look backward for the matching opener. The opener must be the
  // same marker, must be preceded by whitespace / start of node /
  // punctuation, and must be followed by a non-space character.
  let openIdx = -1;
  for (let i = caret - 2; i >= 0; i--) {
    const ch = text[i];
    if (ch === undefined) break;
    if (ch === '\n') break; // don't cross paragraph boundaries
    if (ch !== marker) continue;
    const before: string = i > 0 ? (text[i - 1] ?? '') : '';
    const after: string = i + 1 < text.length ? (text[i + 1] ?? '') : '';
    if (after === ' ' || after === '\t' || after === marker) continue;
    if (
      i === 0 ||
      before === ' ' ||
      before === '\t' ||
      before === '\n' ||
      /[^\p{L}\p{N}]/u.test(before)
    ) {
      openIdx = i;
      break;
    }
  }
  if (openIdx === -1) return false;

  const inner = text.slice(openIdx + 1, caret - 1);
  if (inner.length === 0) return false;
  // Inline code shouldn't recursively format its content — the
  // commit path treats the inner text verbatim.
  return commitInlineAutoFormat(
    node,
    openIdx,
    caret,
    tagName,
    inner,
    /* href */ undefined,
    selection,
  );
}

function tryLinkAutoFormat(
  node: Text,
  caret: number,
  selection: Selection,
): boolean {
  const text = node.nodeValue ?? '';
  if (caret < 5) return false;
  if (text[caret - 1] !== ')') return false;
  // Look backward for `](` then for `[`. Stay in the same line.
  const closeUrlOpen = text.lastIndexOf('](', caret - 2);
  if (closeUrlOpen <= 0) return false;
  const openBracket = text.lastIndexOf('[', closeUrlOpen - 1);
  if (openBracket === -1) return false;
  // No newline can sit between `[` and `)`.
  if (text.slice(openBracket, caret).includes('\n')) return false;
  const label = text.slice(openBracket + 1, closeUrlOpen);
  const url = text.slice(closeUrlOpen + 2, caret - 1);
  if (label.length === 0 || url.length === 0) return false;
  if (/\s/.test(url)) return false;
  return commitInlineAutoFormat(
    node,
    openBracket,
    caret,
    'a',
    label,
    url,
    selection,
  );
}

function tryUrlAutoLink(
  node: Text,
  caret: number,
  selection: Selection,
): boolean {
  const text = node.nodeValue ?? '';
  // Shortest linkable token is a bare domain like `a.co` (4 chars) plus
  // the trailing whitespace → caret 5.
  if (caret < 5) return false;
  // The just-typed whitespace sits at caret - 1.
  // Walk back from caret - 2 to the start of the previous word.
  let start = caret - 2;
  while (start >= 0) {
    const ch = text[start];
    if (ch === ' ' || ch === '\t' || ch === '\n') {
      start++;
      break;
    }
    if (start === 0) break;
    start--;
  }
  if (start < 0) start = 0;
  // The candidate URL spans [start, caret - 1).
  const word = text.slice(start, caret - 1);
  const schemeMatch = URL_RE.exec(word);
  const isSchemeUrl = schemeMatch !== null && schemeMatch[0] === word;
  let isBareDomain = false;
  if (!isSchemeUrl) {
    const bare = BARE_DOMAIN_RE.exec(word);
    isBareDomain =
      bare !== null && COMMON_TLDS.has((bare[1] ?? '').toLowerCase());
  }
  if (!isSchemeUrl && !isBareDomain) return false;
  const href = /^https?:|^mailto:/i.test(word) ? word : `https://${word}`;
  return commitInlineAutoFormat(
    node,
    start,
    caret - 1, // keep the trailing whitespace where the user put it
    'a',
    word,
    href,
    selection,
    /* keepTrailing */ true,
  );
}

/**
 * Replace the text-node slice `[openIdx, closeIdx)` (inclusive of
 * the opener / closer characters being consumed) with `<tag>inner</tag>`
 * — for links pass `href`. The caret lands just after the inserted
 * element (or before the trailing-kept whitespace, when
 * `keepTrailing` is true).
 */
function commitInlineAutoFormat(
  node: Text,
  openIdx: number,
  closeIdx: number,
  tagName: string,
  inner: string,
  href: string | undefined,
  selection: Selection,
  keepTrailing = false,
): boolean {
  const doc = node.ownerDocument;
  if (doc === null) return false;
  const text = node.nodeValue ?? '';
  const before = text.slice(0, openIdx);
  const after = text.slice(closeIdx);
  const parent = node.parentNode;
  if (parent === null) return false;

  const wrapper = doc.createElement(tagName);
  if (href !== undefined) wrapper.setAttribute('href', href);
  wrapper.textContent = inner;

  const frag = doc.createDocumentFragment();
  if (before.length > 0) frag.appendChild(doc.createTextNode(before));
  frag.appendChild(wrapper);
  // For URL autolink we keep the trailing whitespace the user typed;
  // for closer-style auto-formats (`*foo*` → `<strong>foo</strong>`)
  // the closer was consumed — `after` is whatever the user typed past
  // the closer (usually nothing yet).
  if (keepTrailing) {
    // `after` already starts with the whitespace because we passed
    // `closeIdx = caret - 1`. Insert as-is.
    if (after.length > 0) frag.appendChild(doc.createTextNode(after));
  } else if (after.length > 0) {
    frag.appendChild(doc.createTextNode(after));
  }

  parent.replaceChild(frag, node);

  // Anchor the new caret directly on the element we just created + inserted.
  // We MUST NOT re-query the DOM: `parent.querySelector('strong:scope')` never
  // matches (`:scope` is the element itself, not among its own descendants), so
  // the old fallback picked the FIRST same-tag child — placing the caret after
  // an EARLIER `<strong>`/`<em>`/`<a>` and snapping it backwards mid-typing
  // (type `*a* *b*` → caret jumps back after `a`). `wrapper` is unambiguous.
  const range = doc.createRange();
  range.setStartAfter(wrapper);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
}
